import { StyleSheet } from "react-native";
import type { UiTheme } from "../theme";

// Shared StyleSheet for the Tier-2 Progress panels (Density, FatigueDrift,
// IntensityHeatmap, ExerciseFrequency). Lifted from mobile verbatim; the
// card/cardError/empty/errorText/densitySummary rules are genuinely shared
// across panels.
export function makeTierTwoStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    card: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      padding: s.s4,
      gap: s.s3,
    },
    cardError: { borderColor: p.danger },
    cardTitle: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    titleRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    empty: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontStyle: "italic",
    },
    errorText: {
      color: p.danger,
      fontSize: ty.sizes.sm,
    },
    densitySummary: {
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    flagRow: { flexDirection: "row", alignItems: "center", gap: s.s1 },
    densityChart: {
      flexDirection: "row",
      alignItems: "flex-end",
      height: 60,
      gap: 1,
    },
    densityBar: {
      flex: 1,
      borderRadius: 1,
      minHeight: 2,
    },
    fatigueChart: {
      flexDirection: "row",
      alignItems: "flex-end",
      height: 84,
      gap: s.s2,
    },
    fatigueWeek: {
      flex: 1,
      alignItems: "center",
      gap: 2,
    },
    fatigueBarPair: {
      flexDirection: "row",
      alignItems: "flex-end",
      height: 64,
      width: "100%",
      gap: 2,
    },
    fatigueBarRpe: {
      flex: 1,
      backgroundColor: p.brand500,
      borderRadius: 1,
      minHeight: 2,
    },
    fatigueBarRir: {
      flex: 1,
      backgroundColor: p.ink2,
      borderRadius: 1,
      minHeight: 2,
    },
    fatigueWeekLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    fatigueLegend: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontStyle: "italic",
    },
    heatPicker: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: 44,
      backgroundColor: p.surface2,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      gap: s.s2,
    },
    heatPickerPressed: { opacity: 0.7 },
    heatPickerLabel: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
      flex: 1,
    },
    heatPickerChevron: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
    },
    heatPickerList: {
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingVertical: s.s1,
    },
    heatPickerItem: {
      paddingHorizontal: s.s3,
      paddingVertical: s.s3,
      minHeight: 44,
    },
    heatPickerItemPressed: { backgroundColor: p.surface2 },
    heatPickerItemText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
    },
    // "None" / clear row — muted + italic so it reads as a reset, not an
    // exercise (IntensityHeatmap reset-to-empty).
    heatPickerClearText: {
      color: p.ink2,
      fontStyle: "italic",
    },
    heatHeaderRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 2,
    },
    heatRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 2,
    },
    heatAxisLabel: {
      flex: 1,
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontVariant: ["tabular-nums"],
      textAlign: "center",
    },
    heatRowHeader: {
      flex: 0,
      width: 48,
      textAlign: "right",
      paddingRight: s.s1,
    },
    heatCell: {
      flex: 1,
      aspectRatio: 1,
      borderRadius: r.r1,
      borderColor: p.border,
      borderWidth: 1,
      alignItems: "center",
      justifyContent: "center",
    },
    heatCellText: {
      color: p.ink,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    heatFooter: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
      textAlign: "right",
    },
    // Spacing for the <ScaleLegend> band key under the intensity heatmap.
    heatLegend: { marginTop: s.s3 },
    freqRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: s.s2,
      paddingVertical: 2,
    },
    freqLabel: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
      flex: 1,
      minWidth: 0,
    },
    freqValue: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
    },
    freqValueStale: { color: p.danger },
    freqStaleMark: {
      color: p.danger,
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
    },
  });
}
